import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

interface DashboardStats {
  totalUsers: number;
  pendingApprovals: number;
  freelancers: number;
  referrers: number;
  clients: number;
  managers: number;
}

interface User {
  id: string;
  email: string;
  name: string;
  username: string;
  role: 'admin' | 'manager' | 'freelancer' | 'referrer' | 'client';
  is_approved: boolean;
  whatsapp_contact?: string;
  account_number?: string;
  bank_name?: string;
  account_name?: string;
  referrer_id?: string;
  created_at: string;
}

interface GroupedUsers {
  freelancer: User[];
  referrer: User[];
  client: User[];
}

const ManagerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'approvals' | 'users'>('dashboard');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [groupedUsers, setGroupedUsers] = useState<GroupedUsers>({
    freelancer: [],
    referrer: [],
    client: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  const loadUserData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/signin');
        return;
      }

      const response = await axios.get('http://localhost:5000/api/users/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setUser(response.data.data.user);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  }, [navigate]);

  const loadDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      if (activeTab === 'dashboard') {
        const statsResponse = await axios.get('http://localhost:5000/api/admin/dashboard/stats', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setStats(statsResponse.data.data);
      }
      
      if (activeTab === 'approvals') {
        const approvalsResponse = await axios.get('http://localhost:5000/api/admin/users/pending', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setPendingUsers(approvalsResponse.data.data.users);
      }
      
      if (activeTab === 'users') {
        const usersResponse = await axios.get('http://localhost:5000/api/admin/users', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const users: User[] = usersResponse.data.data.users;
        setAllUsers(users);
        
        // Group users by role (only show freelancers, referrers, and clients)
        const grouped: GroupedUsers = {
          freelancer: users.filter((user: User) => user.role === 'freelancer'),
          referrer: users.filter((user: User) => user.role === 'referrer'),
          client: users.filter((user: User) => user.role === 'client')
        };
        setGroupedUsers(grouped);
      }
      
    } catch (error: any) {
      console.error('Error loading manager data:', error);
      setMessage(error.response?.data?.message || 'Error loading data');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleApproveUser = async (userId: string, userName: string) => {
    try {
      const response = await axios.post(
        `http://localhost:5000/api/admin/users/${userId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        setMessage(`User ${userName} has been approved successfully`);
        setPendingUsers(pendingUsers.filter(user => user.id !== userId));
        await loadDashboardData();
      }
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error approving user');
    }
  };

  const handleRejectUser = async (userId: string, userName: string) => {
    if (!window.confirm(`Are you sure you want to reject ${userName}? This action cannot be undone.`)) return;

    try {
      const response = await axios.post(
        `http://localhost:5000/api/admin/users/${userId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        setMessage(`User ${userName} has been rejected`);
        setPendingUsers(pendingUsers.filter(user => user.id !== userId));
      }
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error rejecting user');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'manager': return 'role-manager';
      case 'freelancer': return 'role-freelancer';
      case 'referrer': return 'role-referrer';
      case 'client': return 'role-client';
      default: return 'role-admin';
    }
  };

  const getStatusBadge = (is_approved: boolean) => {
    return is_approved ? 'status-badge approved' : 'status-badge pending';
  };

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'freelancer': return 'Freelancers';
      case 'referrer': return 'Referrers';
      case 'client': return 'Clients';
      default: return role;
    }
  };

  if (isLoading && activeTab === 'dashboard') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading manager dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="flex items-center gap-6">
            <div className="navbar-brand">
              <div className="brand-logo">NS</div>
              <span className="brand-text">NexSync</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <button 
                type="button"
                onClick={() => navigate('/dashboard')}
                className={`nav-link ${location.pathname === '/dashboard' ? 'nav-link-active' : ''}`}
                title="Dashboard"
              >
                Dashboard
              </button>
              <button 
                type="button"
                onClick={() => navigate('/profile')}
                className={`nav-link ${location.pathname === '/profile' ? 'nav-link-active' : ''}`}
                title="Profile"
              >
                Profile
              </button>
              <button 
                type="button"
                onClick={() => navigate('/manager')}
                className={`nav-link ${location.pathname === '/manager' ? 'nav-link-active' : ''}`}
                title="Manager Panel"
              >
                Manager Panel
              </button>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* User Info - Desktop */}
            <div className="hidden md:flex items-center gap-3">
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{user?.username || 'Manager'}</div>
                <div className="flex items-center gap-2">
                  <span className="role-badge role-manager">
                    Manager
                  </span>
                  <span className="status-badge approved">
                    Approved
                  </span>
                </div>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button 
              type="button"
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              onClick={toggleMobileMenu}
              title="Toggle menu"
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Logout Button - Desktop */}
            <button 
              type="button"
              onClick={handleLogout}
              className="hidden md:flex btn btn-ghost btn-sm"
              title="Logout"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-3 space-y-3">
              <button 
                type="button"
                onClick={() => {
                  navigate('/dashboard');
                  setIsMobileMenuOpen(false);
                }}
                className={`nav-link-mobile ${location.pathname === '/dashboard' ? 'nav-link-mobile-active' : ''}`}
                title="Dashboard"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Dashboard
              </button>
              
              <button 
                type="button"
                onClick={() => {
                  navigate('/profile');
                  setIsMobileMenuOpen(false);
                }}
                className={`nav-link-mobile ${location.pathname === '/profile' ? 'nav-link-mobile-active' : ''}`}
                title="Profile"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Profile
              </button>

              <button 
                type="button"
                onClick={() => {
                  navigate('/manager');
                  setIsMobileMenuOpen(false);
                }}
                className={`nav-link-mobile ${location.pathname === '/manager' ? 'nav-link-mobile-active' : ''}`}
                title="Manager Panel"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Manager Panel
              </button>

              {/* Mobile User Info */}
              <div className="px-3 py-2 border-t border-gray-200 mt-3 pt-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{user?.username || 'Manager'}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="role-badge role-manager">
                        Manager
                      </span>
                      <span className="status-badge approved">
                        Approved
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Logout */}
              <button 
                type="button"
                onClick={() => {
                  handleLogout();
                  setIsMobileMenuOpen(false);
                }}
                className="nav-link-mobile text-red-600 hover:text-red-700 hover:bg-red-50"
                title="Logout"
              >
                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Manager Navigation Tabs */}
      <nav className="admin-nav">
        <div className="flex overflow-x-auto">
          <button 
            type="button"
            className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
            title="Dashboard Overview"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Dashboard
          </button>
          <button 
            type="button"
            className={`nav-btn ${activeTab === 'approvals' ? 'active' : ''}`}
            onClick={() => setActiveTab('approvals')}
            title="Pending Approvals"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Pending Approvals
            {stats && stats.pendingApprovals > 0 && (
              <span className="badge">{stats.pendingApprovals}</span>
            )}
          </button>
          <button 
            type="button"
            className={`nav-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
            title="All Users"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            All Users
          </button>
        </div>
      </nav>

      {/* Manager Content */}
      <div className="admin-content">
        {message && (
          <div className={`message ${message.includes('Error') ? 'error-message' : 'success-message'} mb-6`}>
            {message}
          </div>
        )}

        {activeTab === 'dashboard' && stats && (
          <div className="space-y-6">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Manager Dashboard</h1>
              <p className="text-gray-600">Overview of system statistics and user management</p>
            </div>

            <div className="dashboard-cards">
              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Total Users</h3>
                </div>
                <div className="stat-number">{stats.totalUsers}</div>
                <p className="text-sm text-gray-600 mt-2">Registered users in the system</p>
              </div>

              <div className="stat-card warning">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Pending Approvals</h3>
                </div>
                <div className="stat-number">{stats.pendingApprovals}</div>
                <p className="text-sm text-gray-600 mt-2">Users waiting for approval</p>
              </div>

              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Freelancers</h3>
                </div>
                <div className="stat-number">{stats.freelancers}</div>
                <p className="text-sm text-gray-600 mt-2">Active freelancers</p>
              </div>

              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Referrers</h3>
                </div>
                <div className="stat-number">{stats.referrers}</div>
                <p className="text-sm text-gray-600 mt-2">Active referrers</p>
              </div>

              <div className="stat-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Clients</h3>
                </div>
                <div className="stat-number">{stats.clients}</div>
                <p className="text-sm text-gray-600 mt-2">Active clients</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="dashboard-card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="action-buttons">
                <button 
                  type="button"
                  onClick={() => setActiveTab('approvals')}
                  className="btn btn-primary"
                  title="Review Pending Approvals"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Review Approvals
                </button>
                <button 
                  type="button"
                  onClick={() => setActiveTab('users')}
                  className="btn btn-secondary"
                  title="View All Users"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                  View Users
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'approvals' && (
          <div className="approvals-section">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Pending User Approvals</h2>
              <p className="text-gray-600">Review and approve user registration requests</p>
            </div>

            {isLoading ? (
              <div className="loading">Loading pending approvals...</div>
            ) : pendingUsers.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="-38 0 100 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="empty-state-title">No Pending Approvals</h3>
                <p className="empty-state-description">
                  All user registration requests have been processed. Check back later for new requests.
                </p>
              </div>
            ) : (
              <div className="users-grid">
                {pendingUsers.map((user: User) => (
                  <div key={user.id} className="user-card">
                    <div className="user-info">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="user-avatar">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900">{user.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`role-badge ${getRoleBadgeColor(user.role)}`}>
                              {user.role}
                            </span>
                            <span className={getStatusBadge(user.is_approved)}>
                              {user.is_approved ? 'Approved' : 'Pending'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <p><strong className="text-gray-700">ID:</strong> {user.id}</p>
                        <p><strong className="text-gray-700">Email:</strong> {user.email}</p>
                        <p><strong className="text-gray-700">Username:</strong> {user.username}</p>
                        {user.whatsapp_contact && (
                          <p><strong className="text-gray-700">WhatsApp:</strong> {user.whatsapp_contact}</p>
                        )}
                        <p><strong className="text-gray-700">Registered:</strong> {new Date(user.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="user-actions">
                      <button 
                        type="button"
                        onClick={() => handleApproveUser(user.id, user.name)}
                        className="btn btn-success btn-sm"
                        title={`Approve ${user.name}`}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Approve
                      </button>
                      <button 
                        type="button"
                        onClick={() => handleRejectUser(user.id, user.name)}
                        className="btn btn-danger btn-sm"
                        title={`Reject ${user.name}`}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-section">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">All Users</h2>
              <p className="text-gray-600">Manage all registered users in the system</p>
            </div>

            {isLoading ? (
              <div className="loading">Loading users...</div>
            ) : (
              <div className="space-y-8">
                {/* Display users grouped by role */}
                {Object.entries(groupedUsers).map(([role, users]) => (
                  users.length > 0 && (
                    <div key={role} className="role-section">
                      <div className="mb-4">
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          {getRoleDisplayName(role)} ({users.length})
                        </h3>
                        <div className="h-1 w-20 bg-blue-600 rounded"></div>
                      </div>
                      
                      <div className="users-table-container">
                        <table className="users-table">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Name</th>
                              <th>Email</th>
                              <th>Username</th>
                              <th>Status</th>
                              <th>Registered</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.map((user: User) => (
                              <tr key={user.id}>
                                <td className="font-mono text-sm">{user.id}</td>
                                <td className="font-medium">{user.name}</td>
                                <td>{user.email}</td>
                                <td>{user.username}</td>
                                <td>
                                  <span className={getStatusBadge(user.is_approved)}>
                                    {user.is_approved ? 'Approved' : 'Pending'}
                                  </span>
                                </td>
                                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                ))}
                
                {/* Show message if no users found */}
                {allUsers.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-state-icon">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                      </svg>
                    </div>
                    <h3 className="empty-state-title">No Users Found</h3>
                    <p className="empty-state-description">
                      There are no registered users in the system yet.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerDashboard;